import { mapState } from 'pinia'
import { useRootStore } from '@/store/root'
import { formatMoneyWithCurrency } from '@/plugins/numformat'
import { OrderStatus } from '@bigbank/dc-common/clients/http/account/modules/pending-payments/pending-payment.types'
import { toNumber } from 'lodash'
import { AccountRouteName } from '@/modules/account/const'
import { useAccountStore } from '@/modules/account/store/accountStore'
import { usePendingPaymentStore } from '@/modules/account/store/pendingPaymentStore'

const statusColors = {
  [OrderStatus.Confirmed]: 'f-color-mint',
  [OrderStatus.Completed]: 'f-color-mint',
  [OrderStatus.Failed]: 'f-color-red',
  [OrderStatus.Cancelled]: 'f-color-red'
}

export default {
  props: {
    pendingPayments: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      checkboxStates: {}
    }
  },
  computed: {
    ...mapState(useRootStore, ['locale', 'currency']),
    ...mapState(usePendingPaymentStore, ['selectedIban']),
    translations () {
      const signSelected = this.$pgettext('pending_payment_table', 'Accept / Sign selected')
      const sendMoney = this.$pgettext('pending_payment_table', 'Send money')
      const titleWaiting = this.$pgettext('pending_payment', 'Waiting for confirmation')
      const titleApproved = this.$pgettext('pending_payment', 'Approved')
      const titleOther = this.$pgettext('pending_payment', 'Other')

      const confirm = this.isNotConfirmedStatus ? signSelected : sendMoney
      const title = this.isNotConfirmedStatus ? titleWaiting : this.isConfirmedStatus ? titleApproved : titleOther

      return {
        title,
        button: {
          confirm,
          cancelSelected: this.$pgettext('pending_payment_table', 'Cancel selected payments'),
          edit: this.$pgettext('pending_payment_table', 'Edit'),
          delete: this.$pgettext('pending_payment_table', 'Delete'),
          close: this.$pgettext('pending_payment_table', 'Close'),
          recreate: this.$pgettext('pending_payment_table', 'New payment'),
          returnBack: this.$pgettext('pending_payment_sign', 'Return back'),
          accept: this.$pgettext('pending_payment_sign', 'Sign')
        },
        row: {
          actions: this.$pgettext('pending_payment_table', 'Action buttons'),
          selection: this.$pgettext('pending_payment_table', 'Select rows'),
          subPaymentDetails: this.$pgettext('pending_payment_table', 'Payment details row')
        },
        table: {
          selectAllRows: this.$pgettext('pending_payment_table', 'Select all rows'),
          selectRow: this.$pgettext('pending_payment_table', 'Select row'),
          toggleDetails: this.$pgettext('pending_payment_table', 'Toggle details'),
          paymentDetails: this.$pgettext('pending_payment_table', 'Payment details')
        },
        footer: {
          totalAmount: this.$pgettext('pending_payment_table', 'Total amount'),
          totalPayments: this.$pgettext('pending_payment_table', 'Total payments')
        },
        column: {
          fromIban: this.$pgettext('pending_payment_table', 'From account'),
          orderDate: this.$pgettext('pending_payment_table', 'Payment date'),
          referenceNumber: this.$pgettext('pending_payment_table', 'Reference number'),
          description: this.$pgettext('pending_payment_table', 'Description'),
          amount: this.$pgettext('pending_payment_table', 'Amount'),
          orderStatus: this.$pgettext('pending_payment_table', 'Status'),
          acceptance: this.$pgettext('pending_payment_table', 'Acceptance'),
          recipientName: this.$pgettext('pending_payment_table', 'Recipient name'),
          recipientIban: this.$pgettext('pending_payment_table', 'Recipient IBAN')
        },
        status: {
          [OrderStatus.Confirmed]: this.$pgettext('pending_payment_table', 'Approved'),
          [OrderStatus.Completed]: this.$pgettext('pending_payment_table', 'Completed'),
          [OrderStatus.Failed]: this.$pgettext('pending_payment_table', 'Failed'),
          [OrderStatus.Cancelled]: this.$pgettext('pending_payment_table', 'Cancelled'),
          [OrderStatus.NotConfirmed]: this.$pgettext('pending_payment_table', 'Waiting confirmation')
        },
        statusDescription: {
          [OrderStatus.Confirmed]: this.$pgettext('pending_payment_table', 'Waiting for payment date'),
          [OrderStatus.Completed]: this.$pgettext('pending_payment_table', 'Payment has been completed'),
          [OrderStatus.Failed]: this.$pgettext('pending_payment_table', 'Payment processing failed'),
          [OrderStatus.Cancelled]: this.$pgettext('pending_payment_table', 'Payment was cancelled')
        }
      }
    },
    hasTruthyCheckboxes () {
      return Object.values(this.checkboxStates).some(value => value)
    },
    sumAmount () {
      return formatMoneyWithCurrency(this.selectedAmount, this.currency, this.locale)
    },
    selectedAmount () {
      const selectedRows = this.pendingPayments.filter(p => this.checkboxStates[p.id])
      return selectedRows.reduce((acc, row) => acc + row.amount, 0)
    },
    sumForSigning () {
      const amount = this.pendingPayments.reduce((acc, row) => acc + row.amount, 0)
      return formatMoneyWithCurrency(amount, this.currency, this.locale)
    },
    isNotConfirmedStatus () {
      return this.pendingPayments.some(payment => payment.orderStatus === OrderStatus.NotConfirmed)
    },
    isConfirmedStatus () {
      return this.pendingPayments.some(payment => payment.orderStatus === OrderStatus.Confirmed)
    },
    isNotConfirmedOrConfirmedStatus () {
      return !(this.isNotConfirmedStatus || this.isConfirmedStatus)
    },
    tableTestId () {
      return this.isNotConfirmedStatus
        ? 'pending-table-waiting-confirmation'
        : this.isConfirmedStatus ? 'pending-table-confirmed' : 'pending-table-other'
    },
    selectedPendingIds () {
      return Object.entries(this.checkboxStates)
        .filter(([key, value]) => !!value)
        .map(([key, value]) => toNumber(key))
    },
    availableBalanceWarning () {
      // For unknown reasons, mapState will not work with accountStore in unit tests
      const store = useAccountStore()
      const availableBalance = store.paymentAccounts.find(account => account.iban === this.selectedIban)?.availableBalance || 0

      if (this.selectedAmount > availableBalance) {
        return {
          visible: true,
          availableBalance: formatMoneyWithCurrency(availableBalance, this.currency, this.locale),
          selectedIban: this.selectedIban
        }
      }
      return {
        visible: false,
        availableBalance: '',
        selectedIban: ''
      }
    }
  },
  watch: {
    pendingPayments: {
      handler (pendingPayments) {
        this.checkboxStates = pendingPayments.reduce((acc, payment) => {
          acc[payment.id] = null
          return acc
        }, {})
      },
      immediate: true
    }
  },
  methods: {
    onModify (pendingId) {
      this.$emit('onModify', pendingId)
    },
    onCancel (pendingId) {
      this.$emit('onCancel', [pendingId])
    },
    onRecreate (pendingId) {
      this.$router.push({
        name: AccountRouteName.OutgoingPayments,
        query: { pendingPaymentId: pendingId }
      })
    },
    onCancelMany () {
      this.$emit('onCancel', this.selectedPendingIds)
    },
    selectForSigning () {
      this.$router.push({
        name: AccountRouteName.PendingSign,
        query: { pendingPaymentIds: this.selectedPendingIds }
      })
    },
    getPaymentStatusColor (value) {
      return statusColors[value[0]] || 'f-color-gray'
    },
    showModifyButton (status) {
      return [OrderStatus.NotConfirmed, OrderStatus.Confirmed].includes(status)
    },
    showCancelButton (status) {
      return [OrderStatus.NotConfirmed, OrderStatus.Failed, OrderStatus.Confirmed].includes(status)
    },
    showRecreateButton (status) {
      return [OrderStatus.Cancelled, OrderStatus.Failed, OrderStatus.Completed].includes(status)
    },
    goBack () {
      this.$router.push({ name: AccountRouteName.PendingPayments })
    },
    onSign () {
      this.$emit('onSign')
    }
  }
}
